import type { RecurrenceOption } from "../types/status";
import { ensureUtc } from "./formatDate";
import { pad2 } from "./pad2";

export const RECURRENCE_LABELS: Record<RecurrenceOption, string> = {
  none: "Does not repeat",
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
};

export const RECURRENCE_ORDER: RecurrenceOption[] = [
  "none",
  "daily",
  "weekly",
  "biweekly",
  "monthly",
];

const COMMON_TIMEZONES = [
  "Europe/London",
  "Asia/Bangkok",
  "America/New_York",
  "UTC",
];

export function detectTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Common zones plus the viewer's detected zone, de-duplicated, detected first. */
export function timeZoneOptions(detected: string): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const zone of [detected, ...COMMON_TIMEZONES]) {
    if (zone && !seen.has(zone)) {
      seen.add(zone);
      ordered.push(zone);
    }
  }
  return ordered;
}

/**
 * The backend serialises maintenance datetimes as naive UTC wall-clock strings
 * (e.g. "2026-09-20T08:00:00" with no offset). `new Date()` would read those as
 * local time, so force a UTC interpretation by appending "Z" when absent.
 */
export function parseUtcIso(value: string): Date {
  return new Date(ensureUtc(value));
}

/** Wall-clock time in a specific IANA zone, e.g. "09:00 Asia/Bangkok". */
export function timeInZone(date: Date, timeZone: string): string {
  try {
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
    });
  } catch {
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
}

export function localDayKey(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function localTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function nowLocalValue(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function toNaiveIso(localValue: string): string {
  return localValue.length === 16 ? `${localValue}:00` : localValue;
}
